/**
 * Shared utilities for feature flags
 */

import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { ADMIN_COLORS } from '@/constants/admin';

/**
 * Filter feature flags by search term
 */
export const filterFeatureFlags = (
  flags: FeatureFlagAttributes[],
  searchTerm: string
): FeatureFlagAttributes[] => {
  if (!searchTerm.trim()) return flags;
  
  const searchLower = searchTerm.toLowerCase();
  return flags.filter((flag: FeatureFlagAttributes) =>
    (flag.propertyName?.toLowerCase() || '').includes(searchLower) ||
    (flag.organizationName?.toLowerCase() || '').includes(searchLower) ||
    Object.entries(flag.flags).some(
      ([key, value]) =>
        key.toLowerCase().includes(searchLower) ||
        value.toString().toLowerCase().includes(searchLower)
    )
  );
};

/**
 * Group feature flags by organization name
 */
export const groupFlagsByOrganization = (
  flags: FeatureFlagAttributes[]
): Record<string, FeatureFlagAttributes[]> => {
  const grouped: Record<string, FeatureFlagAttributes[]> = {};
  
  flags.forEach((flag: FeatureFlagAttributes) => {
    const orgName = flag.organizationName || 'Unknown';
    if (!grouped[orgName]) {
      grouped[orgName] = [];
    }
    grouped[orgName].push(flag);
  });
  
  return grouped;
};

/**
 * Get organization dot color based on organization name
 */
export const getOrgDotColor = (orgName: string): string => {
  if (orgName === 'Core Spaces') return ADMIN_COLORS.PRIMARY;
  if (orgName === 'GMH University Housing') return ADMIN_COLORS.SUCCESS;
  return ADMIN_COLORS.TEXT_TERTIARY;
};

/**
 * Extract organization ID from feature flag (handles both camelCase and snake_case)
 */
export const getOrganizationId = (flag: FeatureFlagAttributes): number | undefined => {
  return (flag as any)?.organizationId || flag?.organization_id;
};

/**
 * Get property ID from feature flag
 */
export const getPropertyId = (flag: FeatureFlagAttributes): number | undefined => {
  return flag.property_id || flag.id;
};

/**
 * Calculate override count for a feature flag
 */
export const getOverrideCount = (flag: FeatureFlagAttributes): number => {
  return Object.keys(flag.flags).length;
};

/**
 * Format override count text
 */
export const formatOverrideText = (count: number): string => {
  return `${count} override${count !== 1 ? 's' : ''}`;
};

